import { Form, Modal, Select, Space, Spin } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import ValuerTypeEnum from '~Api/Application/ValuerTypeEnum';
import { loanDrawdownGetAction, loanDrawdownQsInstructedAction } from '~Loans/actions';
import { loanDrawdownSelector } from '~Loans/selectors';
import { IGlobalState } from '~reducer';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import { IDictionary } from '~utilities/IDictionary';
import IValuer from '~Valuers/IValuer';
import IGroupedSelectOption from '~utilities/IGroupedSelectOption';
import { valuersSelector } from '~Valuers/selectors';
import { valuersListAction } from '~Valuers/actions';

export default function DrawdownQsInstructedModal(props: {
    loanDrawdownUuid: string;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { loanDrawdownUuid, isOpen, onCancel } = props;

    const [ qsInstructedTime, setQsInstructedTime ] = useState<string>(null);
    const [ valuerUuid, setValuerUuid ] = useState<string>(null);
    const [ errors, setErrors ] = useState<IDictionary<string>>({});

    const drawdown: ILoanDrawdown = useSelector(
        (state: IGlobalState): ILoanDrawdown => loanDrawdownSelector(state, loanDrawdownUuid),
    );
    const valuers: IDictionary<IValuer> = useSelector(valuersSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!valuers) {
            dispatch(valuersListAction());
        }
    }, [
        dispatch,
        valuers,
    ]);

    useEffect((): void => {
        if (!drawdown) {
            dispatch(loanDrawdownGetAction(loanDrawdownUuid));
        }
    }, [
        drawdown,
        dispatch,
        loanDrawdownUuid,
    ]);

    const validateQsInstructedTime: () => boolean = useCallback((): boolean => {
        let error: string = null;

        if (!qsInstructedTime) {
            error = 'Please enter the date the QS was instructed';
        }

        setErrors({
            ...errors,
            qsInstructedTime: error,
        });

        return !error;
    }, [
        errors,
        qsInstructedTime,
    ]);

    const disabledTime: (time: Dayjs) => boolean = useCallback((time: Dayjs): boolean => {
        return time && (time.isAfter(dayjs(), 'second') || time.isBefore(dayjs(drawdown.requestedTime)));
    }, [ drawdown.requestedTime ]);

    const onClickOk: () => void = useCallback((): void => {
        if (!validateQsInstructedTime()) {
            return;
        }

        dispatch(loanDrawdownQsInstructedAction(
            drawdown.uuid,
            qsInstructedTime,
            valuerUuid,
        ));
        onCancel();
    }, [
        drawdown,
        dispatch,
        qsInstructedTime,
        onCancel,
        validateQsInstructedTime,
        valuerUuid,
    ]);

    const onChangeQsInstructedDate: (date: Dayjs) => void = useCallback((date: Dayjs): void => {
        setQsInstructedTime(date ? dayjs(`${date.format('YYYY-MM-DD')} ${dayjs(qsInstructedTime || undefined).format('HH:mm')}`).format() : null);
    }, [ qsInstructedTime ]);

    const onChangeQsInstructedTime: (time: Dayjs) => void = useCallback((time: Dayjs): void => {
        setQsInstructedTime(time ? dayjs(`${dayjs(qsInstructedTime || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`).format() : null);
    }, [ qsInstructedTime ]);

    const onChangeValuer: (valuerUuid: string) => void = useCallback((valuerUuid: string): void => {
        setValuerUuid(valuerUuid || null);
    }, []);

    if (!drawdown || !valuers) {
        return (
            <Modal
                footer={false}
                onCancel={onCancel}
                open={isOpen}
                title='QS Instructed'
                wrapClassName='loan-drawdown-qs-instructed-modal'
                width={640}
            >
                <Spin />
            </Modal>
        );
    }

    const sortedValuers: IValuer[] = _.sortBy(valuers, [ 'state', 'name' ]);
    const groupedValuers: IDictionary<IGroupedSelectOption> = {};

    _.each(sortedValuers, (valuer: IValuer): void => {
        if (!groupedValuers[valuer.state]) {
            groupedValuers[valuer.state] = {
                label: valuer.state,
                options: [],
                title: valuer.state,
            };
        }

        groupedValuers[valuer.state].options.push({
            label: valuer.name,
            value: valuer.uuid,
        });
    });

    const groupedValuerOptions: (IGroupedSelectOption | DefaultOptionType)[] = [
        {
            label: '',
            value: '',
        },
        ..._.values(groupedValuers),
    ];

    return (
        <Modal
            okText='QS Instructed'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='QS Instructed'
            wrapClassName='loan-drawdown-qs-instructed-modal'
            width={640}
        >
            <Form.Item
                label='Date QS Instructed'
                className='date-qs-instructed'
                help={errors.qsInstructedTime}
                validateStatus={errors.qsInstructedTime && 'error'}
            >
                <Space>
                    <DatePicker
                        className='qs-instructed-pick-date'
                        disabledDate={disabledTime}
                        onChange={onChangeQsInstructedDate}
                        format='DD/MM/YYYY'
                        value={qsInstructedTime ? dayjs(qsInstructedTime) : null}
                    />
                    <TimePicker
                        className='qs-instructed-pick-time'
                        disabledDate={disabledTime}
                        onChange={onChangeQsInstructedTime}
                        value={qsInstructedTime ? dayjs(qsInstructedTime) : null}
                        format='H:mm'
                    />
                </Space>
            </Form.Item>
            <Form.Item
                label='Quantity Surveyor'
                className='quantity-surveyor'
            >
                <Select
                    value={valuerUuid}
                    onChange={onChangeValuer}
                    options={groupedValuerOptions}
                />
            </Form.Item>
        </Modal>
    );
}
